import { crfPartialMessage, crfReadinessMessage, type CrfReadiness } from './crfReadiness';

/**
 * What a document row does about the "at least one approved case report form"
 * rule. Pure, so the decision is testable; the hub, the Requirements & Design
 * card and the Testing & Qualification card all ask it.
 *
 * Jim's rule: partial approval ALLOWS generation with a warning; zero approved
 * blocks with an explanation. While readiness is still loading the row is held
 * (no enabled Generate) but says nothing about forms being missing.
 */
export const NO_APPROVED_FORMS_REASON =
  'No case report forms are approved. Approve them in the CRF Builder first.';

export interface ApprovedFormsGate {
  /** Generate and Regenerate must be disabled. */
  blocked: boolean;
  /** Why, for the button title. Null when not blocked. */
  reason: string | null;
  /** Amber warning for the partial state. Null otherwise. */
  warning: string | null;
}

const OPEN: ApprovedFormsGate = { blocked: false, reason: null, warning: null };

/** `applies` is whether this document's generator refuses on zero approved forms. */
export function approvedFormsGate(crf: CrfReadiness, applies: boolean): ApprovedFormsGate {
  if (!applies) return OPEN;
  if (crf.state === 'unknown') {
    return { blocked: true, reason: crfReadinessMessage(crf), warning: null };
  }
  if (!crf.canGenerate) {
    return { blocked: true, reason: NO_APPROVED_FORMS_REASON, warning: null };
  }
  return { blocked: false, reason: null, warning: crf.partial ? crfPartialMessage(crf) : null };
}

export interface StudyBuildBlockedInput {
  canGenerate: boolean;
  /** Shown when the role is the blocker. */
  roleMessage: string;
  gate: ApprovedFormsGate;
  /** Undefined means this row has no Data Dictionary rule. */
  ddReady?: boolean;
  ddMessage?: string;
}

/**
 * The first thing in the way, or null. Order: role (the user cannot act on
 * anything else), then approved forms (the root cause: the Data Dictionary is
 * itself generated from approved forms, so naming the dictionary first would
 * send the user to a page that is also blocked), then the Data Dictionary.
 */
export function studyBuildBlockedReason(i: StudyBuildBlockedInput): string | null {
  if (!i.canGenerate) return i.roleMessage;
  if (i.gate.blocked) return i.gate.reason;
  if (i.ddReady === false) return i.ddMessage ?? null;
  return null;
}
